import { BRAND } from "@bigpp/config/brand";
import { CreditCard, ExternalLink, LogOut, Moon, Sun, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@bigpp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@bigpp/ui/components/dialog";

import { env } from "../env.ts";
import {
  clearLocalData,
  computeLocalDataSize,
  formatBytes,
} from "../local-data.ts";
import { signOut } from "../sign-out.ts";
import { useTheme } from "../theme.ts";
import { trpc } from "../trpc.ts";
import { ConfirmDialog } from "./ConfirmDialog.tsx";

type RowProps = { title: string; hint?: string; action: React.ReactNode };

const Row = ({ title, hint, action }: RowProps) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex min-w-0 flex-col">
      <span className="text-[13px] font-medium">{title}</span>
      {hint && (
        <span className="text-[11px] text-[var(--color-muted-foreground)]">{hint}</span>
      )}
    </div>
    <div className="shrink-0">{action}</div>
  </div>
);

export function SettingsDialog({
  open,
  onOpenChange,
  displayName,
  email,
  credits,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  displayName: string;
  email: string;
  credits: number;
}) {
  const { theme, toggle } = useTheme();
  const [clearOpen, setClearOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [usage, setUsage] = useState<{ bytes: number; snapshots: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setUsage(null);
    void computeLocalDataSize().then((u) => {
      if (!cancelled) setUsage(u);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const openWebProfile = () => {
    window.open(`${env.VITE_WEB_URL}/account`, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Settings</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 text-sm">
            <Row
              title="Signed in as"
              hint={displayName === email ? email : `${displayName} · ${email}`}
              action={
                <span className="rounded-full bg-[var(--color-secondary)] px-2 py-0.5 text-[10px] text-[var(--color-muted-foreground)]">
                  account
                </span>
              }
            />

            <div className="h-px bg-[var(--color-border)]" />

            <CreditsSection fallbackTotal={credits} />

            <TopUpSection />

            <div className="h-px bg-[var(--color-border)]" />

            <Row
              title="Appearance"
              hint="Switch between light and dark mode."
              action={
                <Button type="button" variant="outline" size="sm" onClick={toggle}>
                  {theme === "dark" ? <Sun /> : <Moon />}
                  {theme === "dark" ? "Light" : "Dark"}
                </Button>
              }
            />

            <div className="h-px bg-[var(--color-border)]" />

            <Row
              title="Profile & billing"
              hint="Manage your account and billing online."
              action={
                <Button type="button" variant="outline" size="sm" onClick={openWebProfile}>
                  <ExternalLink />
                  Open
                </Button>
              }
            />

            <div className="h-px bg-[var(--color-border)]" />

            <Row
              title="Clear local data"
              hint={
                usage === null
                  ? "Deletes cached deck snapshots and pinned-skill preferences on this device."
                  : usage.snapshots === 0
                    ? "No cached deck snapshots on this device."
                    : `${usage.snapshots} snapshot${usage.snapshots === 1 ? "" : "s"} — ${formatBytes(usage.bytes)} on this device.`
              }
              action={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={usage !== null && usage.bytes === 0}
                  onClick={() => {
                    setClearOpen(true);
                  }}
                >
                  <Trash2 />
                  Clear
                </Button>
              }
            />

            <div className="h-px bg-[var(--color-border)]" />

            <Row
              title="Sign out"
              hint={`You'll need to sign in again to use ${BRAND}.`}
              action={
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setSignOutOpen(true);
                  }}
                >
                  <LogOut />
                  Sign out
                </Button>
              }
            />
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={clearOpen}
        onOpenChange={(v) => {
          if (!pending) setClearOpen(v);
        }}
        title="Clear local data?"
        description={
          usage && usage.bytes > 0
            ? `This removes ${usage.snapshots} snapshot${usage.snapshots === 1 ? "" : "s"} (${formatBytes(usage.bytes)}) and pinned-skill preferences from this device. It cannot be undone.`
            : "This removes cached deck snapshots and pinned-skill preferences from this device. It cannot be undone."
        }
        confirmLabel={pending ? "Clearing…" : "Clear"}
        destructive
        onConfirm={async () => {
          setPending(true);
          try {
            await clearLocalData();
            toast.success("Local data cleared");
            setClearOpen(false);
            setUsage({ bytes: 0, snapshots: 0 });
          } catch (e) {
            toast.error(
              e instanceof Error
                ? e.message
                : "Couldn't clear device data. Try again, or contact support if it keeps failing.",
            );
          } finally {
            setPending(false);
          }
        }}
      />

      <ConfirmDialog
        open={signOutOpen}
        onOpenChange={(v) => {
          if (!pending) setSignOutOpen(v);
        }}
        title="Sign out?"
        description={`You'll need to sign in again to use ${BRAND}.`}
        confirmLabel={pending ? "Signing out…" : "Sign out"}
        destructive
        onConfirm={async () => {
          setPending(true);
          try {
            await signOut();
          } catch (e) {
            setPending(false);
            toast.error(
              e instanceof Error
                ? e.message
                : "Sign-out didn't complete. Refresh the task pane and try again.",
            );
          }
        }}
      />
    </>
  );
}

// Live bucket breakdown. Falls back to the parent's aggregate value while the
// query is in flight so the row is never blank.
function CreditsSection({ fallbackTotal }: { fallbackTotal: number }) {
  const q = trpc.billing.balance.useQuery(undefined, { staleTime: 30_000 });
  const b = q.data;

  const total = b ? b.total : fallbackTotal;
  const parts: string[] = [];
  if (b) {
    if (b.monthly > 0) parts.push(`${b.monthly.toLocaleString()} monthly`);
    if (b.trial > 0) parts.push(`${b.trial.toLocaleString()} trial`);
    if (b.topup > 0) parts.push(`${b.topup.toLocaleString()} top-up`);
  }
  const hint = b
    ? parts.length > 0
      ? parts.join(" · ")
      : "Out of credits — top up to keep going."
    : `Spent each time ${BRAND} runs a turn.`;

  return (
    <Row
      title="Credits"
      hint={hint}
      action={<span className="text-sm font-semibold">{total.toLocaleString()}</span>}
    />
  );
}

// Opens Stripe Checkout in a new tab. The webhook grants credits on success;
// the balance query above refetches on window focus so users see the balance
// bump automatically when they come back to the pane.
function TopUpSection() {
  const plans = trpc.billing.plans.useQuery(undefined, { staleTime: 5 * 60_000 });
  const startCheckout = trpc.billing.startCheckout.useMutation({
    onSuccess: (res) => {
      window.open(res.url, "_blank", "noopener,noreferrer");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const topups = plans.data?.topups ?? [];
  const paymentsEnabled = plans.data?.paymentsEnabled ?? false;

  if (!plans.data) {
    return <Row title="Buy credits" hint="Loading plans…" action={null} />;
  }
  if (!paymentsEnabled || topups.length === 0) {
    return (
      <Row
        title="Buy credits"
        hint="Payments are temporarily unavailable — check back soon."
        action={null}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex min-w-0 flex-col">
        <span className="text-[13px] font-medium">Buy credits</span>
        <span className="text-[11px] text-[var(--color-muted-foreground)]">
          Top-ups never expire. Pick a bundle.
        </span>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {topups.map((t) => (
          <Button
            key={t.key}
            type="button"
            variant="outline"
            size="sm"
            disabled={startCheckout.isPending}
            onClick={() => {
              startCheckout.mutate({ planKey: t.key, origin: "addin" });
            }}
            className="h-auto flex-col gap-0.5 py-1.5"
          >
            <span className="flex items-center gap-1 text-[12px] font-semibold">
              <CreditCard className="size-3" />${t.displayUsd}
            </span>
            <span className="text-[10px] text-[var(--color-muted-foreground)]">
              {t.credits.toLocaleString()} credits
            </span>
          </Button>
        ))}
      </div>
    </div>
  );
}
